import { Component, computed, inject, input, signal, ViewChild, ElementRef, DestroyRef } from '@angular/core';
import { TableModule } from '@openng/optimus-ui/table';
import { CoBuyer, CoBuyerPhone, UpdateCoBuyer } from '../../models/co-buyer.models';
import { SalesService } from '../../services/sales.service';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalService, PhoneNumbersDisplayComponent, rxResourceFixed } from '@zayan-erp/shared';
import { AddCoBuyerModalComponent } from '../add-co-buyer-modal/add-co-buyer-modal.component';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SalesApsService } from '../../services/sales-aps.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'co-buyers-list',
  imports: [TableModule, PhoneNumbersDisplayComponent, AddCoBuyerModalComponent, NgbTooltip],
  templateUrl: './co-buyers-list.component.html'
})
export class CoBuyersListComponent {
  @ViewChild('addCoBuyerModal') addCoBuyerModal!: ElementRef;
  readonly #salesService = inject(SalesService);
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #destroyRef = inject(DestroyRef);
  readonly _salesApsService = inject(SalesApsService);
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly selectedCoBuyer = signal<string | null>(null);
  readonly prospectId = input<string | null>(this.#activatedRoute.snapshot.paramMap.get('id') ?? null);

  readonly coBuyersResource = rxResourceFixed({
    params: () => ({ prospectId: this.prospectId() }),
    stream: ({ params }) => (params.prospectId ? this.#salesService.readCoBuyers(params.prospectId, true) : of([]))
  });

  readonly coBuyers = computed(() => this.coBuyersResource.value() ?? []);

  openAddCoBuyerModal(): void {
    this.selectedCoBuyer.set(null);
    this.#modalService.open(this.addCoBuyerModal, { ...this.#ngbModalService.centerModal, size: 'xl' });
  }

  openEditCoBuyerModal(coBuyerId: string): void {
    this.selectedCoBuyer.set(coBuyerId);
    this.#modalService.open(this.addCoBuyerModal, { ...this.#ngbModalService.centerModal, size: 'xl' });
  }

  toggleCoBuyerStatus(coBuyer: CoBuyer, event: Event): void {
    if (!coBuyer.id) return;

    const target = event.target as HTMLInputElement;
    const previousStatus = !target.checked;

    this.#salesService
      .toggleCoBuyerStatus(coBuyer.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.coBuyersResource.reload(),
        error: () => (target.checked = previousStatus)
      });
  }

  onCoBuyerReload(): void {
    this.coBuyersResource.reload();
  }

  getFullName(coBuyer: CoBuyer): string {
    return [coBuyer.title, coBuyer.firstName, coBuyer.lastName].filter(Boolean).join(' ') || 'N/A';
  }

  getPhoneContacts(coBuyer: CoBuyer): CoBuyerPhone[] {
    return coBuyer.phone ? [coBuyer.phone] : [];
  }

  onToggleAddToMailingList(coBuyer: UpdateCoBuyer, event: Event): void {
    if (!coBuyer.id) return;

    const target = event.target as HTMLInputElement;
    const previousStatus = target.checked;
    coBuyer.addToMailingList = target.checked;
    this.#salesService
      .updateCoBuyer(coBuyer.id, coBuyer)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.coBuyersResource.reload(),
        error: () => (target.checked = previousStatus)
      });
  }

  onToggleIncludeInContract(coBuyer: UpdateCoBuyer, event: Event): void {
    if (!coBuyer.id) return;

    const target = event.target as HTMLInputElement;
    const previousStatus = target.checked;
    coBuyer.includeInContract = target.checked;
    this.#salesService
      .updateCoBuyer(coBuyer.id, coBuyer)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.coBuyersResource.reload(),
        error: () => (target.checked = previousStatus)
      });
  }
}
